import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import type { BulkActionResult } from '../../../../../js/api';
import { HealthcareEntitiesService } from '../../../../../js/api';
import { Button } from '../../../components/button';
import { Checkbox } from '../../../components/checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { DropdownSelect } from '../../../components/dropdown-select';
import { Field, Label } from '../../../components/fieldset';
import { useCodeDefinitionsAsOptions } from '../../../hooks/useCodeDefinitions';
import { useRowSelection } from '../../../hooks/useRowSelection';
import { pluralize } from './contactDisplay';
import { LinkPickerList } from './LinkPickerList';
import { NewContactInlineForm } from './NewContactInlineForm';
import { NotificationToggle } from './NotificationToggle';
import { contactQueryKeys, invalidateContactQueries } from './queryKeys';
import {
  addPickerEmptyText,
  apiErrorMessage,
  isRemoveKind,
  LINKS_LIMIT,
  LinkDialogMode,
  LinkDirection,
  linkDialogTitle,
  PickerKind,
  pickerKindFor,
  useLinkDialogData,
} from './useLinkDialogData';

const ROLE_CATEGORY = 'FacilityContactRoles';

type RoleCode = Parameters<
  typeof HealthcareEntitiesService.healthcareEntitiesFacilityContactsBulkLinkCreate
>[0]['requestBody']['role'];

export type LinkContactsDialogProps = {
  open: boolean;
  onClose: () => void;
  mode: LinkDialogMode;
  /** Add mode: which side the caller's selection fixes. */
  direction?: LinkDirection;
  contactIds?: string[];
  facilityIds?: string[];
  /** Acting-as org; the add-mode pickers are limited to it. */
  healthcareEntityId?: string;
  /** Name for the title when exactly one contact (or, facilities → contacts, one facility) is selected. */
  subjectName?: string;
  onDone?: () => void;
};

type LinkRequest = {
  kind: PickerKind;
  contactIds: string[];
  facilityIds: string[];
  picked: string[];
  role: string | null;
  receiveNotifications: boolean;
  allFacilities: boolean;
};

const submitLinkRequest = (request: LinkRequest): Promise<BulkActionResult> => {
  const { kind, contactIds, facilityIds, picked, role, receiveNotifications, allFacilities } =
    request;
  if (kind === 'linkedFacilities') {
    return HealthcareEntitiesService.healthcareEntitiesFacilityContactsBulkUnlinkCreate({
      requestBody: {
        contact_ids: contactIds,
        facility_ids: allFacilities ? undefined : picked,
        role: (role ?? undefined) as RoleCode | undefined,
      },
    });
  }
  if (kind === 'linkedContacts') {
    // Only at the selected facilities — never a contact's other links.
    return HealthcareEntitiesService.healthcareEntitiesFacilityContactsBulkUnlinkCreate({
      requestBody: {
        contact_ids: picked,
        facility_ids: facilityIds,
        role: (role ?? undefined) as RoleCode | undefined,
      },
    });
  }
  return HealthcareEntitiesService.healthcareEntitiesFacilityContactsBulkLinkCreate({
    requestBody: {
      contact_ids: kind === 'contacts' ? picked : contactIds,
      facility_ids: kind === 'facilities' ? picked : facilityIds,
      role: role as RoleCode,
      receive_notifications: receiveNotifications,
    },
  });
};

const resultMessage = (kind: PickerKind, result: BulkActionResult) => {
  const links = pluralize(result.processed, 'facility link');
  if (isRemoveKind(kind)) return `Removed ${links}`;
  return result.skipped ? `Added ${links} · ${result.skipped} already existed` : `Added ${links}`;
};

const canSubmitRequest = (request: LinkRequest) => {
  if (!isRemoveKind(request.kind)) {
    return Boolean(request.role) && request.picked.length > 0;
  }
  if (request.kind === 'linkedFacilities' && request.allFacilities) return Boolean(request.role);
  return request.picked.length > 0;
};

/**
 * One dialog for linking contacts and facilities in bulk, in either direction,
 * and for removing links in either direction (remove mode lists only the
 * selected contacts' linked facilities, or the selected facilities' linked
 * contacts).
 */
export const LinkContactsDialog: React.FC<LinkContactsDialogProps> = ({
  open,
  onClose,
  mode,
  direction = 'contactsToFacilities',
  contactIds = [],
  facilityIds = [],
  healthcareEntityId,
  subjectName,
  onDone,
}) => {
  const queryClient = useQueryClient();
  const kind = pickerKindFor(mode, direction);
  const isRemove = isRemoveKind(kind);
  // "All of their facilities" only makes sense when the contacts are the subject.
  const canRemoveAll = kind === 'linkedFacilities';
  const { data: roleOptions } = useCodeDefinitionsAsOptions(ROLE_CATEGORY);
  const roleTitles = useMemo(
    () => new Map((roleOptions ?? []).map((option) => [option.value, option.label])),
    [roleOptions]
  );
  const roleLabel = useCallback((code: string) => roleTitles.get(code) ?? code, [roleTitles]);
  const picker = useRowSelection(open ? 'open' : 'closed');
  const [search, setSearch] = useState('');
  const [role, setRole] = useState<string | null>(null);
  const [receiveNotifications, setReceiveNotifications] = useState(true);
  const [allFacilities, setAllFacilities] = useState(false);
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setRole(null);
    setReceiveNotifications(true);
    setAllFacilities(false);
    setIsCreatingContact(false);
    setErrorMessage(null);
  }, [open]);

  const { items, hiddenIds, isLoading, truncated } = useLinkDialogData({
    open,
    kind,
    contactIds,
    facilityIds,
    healthcareEntityId,
    search,
    role,
    roleLabel,
  });
  // Picks hidden by the chosen role (already held) never go in the request.
  const visiblePicked = useMemo(
    () => new Set(picker.selectedIds.filter((id) => !hiddenIds.has(id))),
    [picker.selectedIds, hiddenIds]
  );

  const request: LinkRequest = {
    kind,
    contactIds,
    facilityIds,
    picked: Array.from(visiblePicked).filter(
      (id) => !isRemove || items.some((item) => item.id === id)
    ),
    role,
    receiveNotifications,
    allFacilities,
  };

  const mutation = useMutation({
    mutationFn: () => submitLinkRequest(request),
    onSuccess: async (result) => {
      toast.success(resultMessage(kind, result));
      await invalidateContactQueries(queryClient);
      onDone?.();
      onClose();
    },
    onError: (error) => setErrorMessage(apiErrorMessage(error)),
  });

  const canSubmit = canSubmitRequest(request);
  const noun = kind === 'contacts' ? 'contacts' : 'facilities';

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (canSubmit) mutation.mutate();
        }}
        data-testid="link-contacts-dialog"
      >
        <DialogTitle>
          {linkDialogTitle(kind, contactIds.length, facilityIds.length, subjectName)}
        </DialogTitle>
        <DialogBody>
          <div className="tw:space-y-4">
            <Field>
              <Label htmlFor="link-contacts-role">
                Role
                {!isRemove && <span className="tw:text-stoplight-danger">*</span>}
              </Label>
              <DropdownSelect
                inputId="link-contacts-role"
                aria-label="Role"
                allOptions={roleOptions}
                value={role}
                onChange={(option) =>
                  setRole(option && !Array.isArray(option) ? option.value : null)
                }
                placeholder={isRemove ? 'Any role' : 'Select a role'}
                isClearable={isRemove}
              />
            </Field>

            {canRemoveAll && (
              // The wrapper takes the space-y gap; the label resets Bootstrap's label
              // display/margin with ! (unlayered CSS beats Tailwind's layers).
              <div>
                <label
                  className="tw:!flex tw:!mb-0 tw:items-center tw:gap-2 tw:text-sm"
                  htmlFor="link-all"
                >
                  <Checkbox
                    id="link-all"
                    color="brandgreen"
                    checked={allFacilities}
                    onChange={setAllFacilities}
                    data-testid="link-contacts-all-facilities"
                  />
                  All of their facilities
                </label>
              </div>
            )}

            {!(canRemoveAll && allFacilities) && (
              <LinkPickerList
                items={items}
                picked={visiblePicked}
                onToggle={picker.toggle}
                onSetMany={picker.setMany}
                isLoading={isLoading}
                emptyText={
                  isRemove
                    ? `No linked ${kind === 'linkedContacts' ? 'contacts' : 'facilities'} match.`
                    : addPickerEmptyText(
                        kind,
                        search,
                        hiddenIds.size,
                        kind === 'contacts' ? facilityIds.length : contactIds.length
                      )
                }
                search={isRemove ? undefined : search}
                onSearchChange={isRemove ? undefined : setSearch}
                searchPlaceholder={`Search ${noun}`}
                testId="link-contacts-picker"
              />
            )}

            {kind === 'contacts' && isCreatingContact && (
              <NewContactInlineForm
                onCreated={(contactId) => {
                  setIsCreatingContact(false);
                  picker.setMany([contactId], true);
                  queryClient.invalidateQueries({
                    queryKey: [...contactQueryKeys.all, 'picker', 'contacts'],
                  });
                }}
                onCancel={() => setIsCreatingContact(false)}
              />
            )}
            {!isRemove && (
              <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
                <NotificationToggle
                  checked={receiveNotifications}
                  onChange={setReceiveNotifications}
                  ariaLabel="Receive notifications"
                  label="Receive notifications"
                  data-testid="link-contacts-notifications"
                />
                {kind === 'contacts' && !isCreatingContact && (
                  <Button
                    plain
                    small
                    onClick={() => setIsCreatingContact(true)}
                    data-testid="link-contacts-new-contact"
                  >
                    <i className="far fa-plus tw:mr-1" />
                    New contact
                  </Button>
                )}
              </div>
            )}

            {canRemoveAll && allFacilities && !role && (
              <p className="tw:text-sm tw:text-zinc-500" data-testid="link-contacts-narrow-hint">
                Choose a role or specific facilities. To remove every link, delete the contact.
              </p>
            )}
            {truncated && (
              <p className="tw:text-sm tw:text-amber-700" data-testid="link-contacts-truncated">
                Only the first {LINKS_LIMIT} facility links are shown. Narrow your selection to see
                every one.
              </p>
            )}
            {errorMessage && (
              <p className="tw:text-sm tw:text-stoplight-danger" data-testid="link-contacts-error">
                {errorMessage}
              </p>
            )}
          </div>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={onClose} disabled={mutation.isLoading}>
            Cancel
          </Button>
          <Button
            type="submit"
            color={isRemove ? 'stoplight-danger' : 'brandgreen1'}
            disabled={!canSubmit || mutation.isLoading}
            data-testid="link-contacts-submit"
          >
            {isRemove ? 'Remove' : 'Add'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
